import { useEffect, useRef, useState } from "react";
import { Send, Activity, Globe, CheckCircle2 } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal, Eyebrow } from "./common";
import { TiScene } from "../lib/tiScene";

const DEST_KEYS = ["server", "stations", "wifi", "cctv"];

export default function Ti3D() {
    const { lang, t } = useLang();
    const canvasRef = useRef(null);
    const sectionRef = useRef(null);
    const sceneRef = useRef(null);
    const [dest, setDest] = useState("server");
    const [tele, setTele] = useState({ packets: 0, latency: null, dest: null });

    useEffect(() => {
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const scene = new TiScene(canvasRef.current, {
            onPacket: (info) =>
                setTele((p) => ({ ...p, latency: info.latency })),
        });
        sceneRef.current = scene;

        const container = canvasRef.current.parentElement;
        const ro = new ResizeObserver(() =>
            scene.resize(container.clientWidth, container.clientHeight)
        );
        ro.observe(container);
        scene.resize(container.clientWidth, container.clientHeight);

        const io = new IntersectionObserver(
            ([e]) => (scene.inView = e.isIntersecting),
            { threshold: 0.12 }
        );
        io.observe(sectionRef.current);

        return () => {
            scene.dispose();
            ro.disconnect();
            io.disconnect();
        };
    }, []);

    useEffect(() => {
        sceneRef.current?.setLabels(t.ti3d.labels);
    }, [lang, t]);

    const send = () => {
        setTele((p) => ({ ...p, packets: p.packets + 1, dest }));
        sceneRef.current?.sendPacket(dest, true);
    };

    return (
        <div ref={sectionRef} className="mt-20" data-testid="ti-3d-section">
            <Reveal className="mb-10 max-w-2xl">
                <h3 className="mt-4 font-display font-extrabold tracking-tight text-2xl sm:text-3xl lg:text-4xl leading-[1.08]">
                    {t.ti3d.title}
                </h3>
                <p className="mt-4 text-slate-400">{t.ti3d.sub}</p>
            </Reveal>

            <div className="grid lg:grid-cols-[1.5fr_1fr] gap-6 items-stretch">
                <Reveal className="relative">
                    <div
                        data-testid="ti-3d-canvas"
                        className="relative rounded-2xl border border-cyan-400/20 bg-obsidian overflow-hidden h-[380px] md:h-[500px]"
                    >
                        <canvas ref={canvasRef} className="block w-full h-full" />
                        <div className="absolute top-4 left-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 bg-obsidian/70 backdrop-blur px-3 py-1.5 rounded-full border border-cyan-400/25">
                            <span className="w-1.5 h-1.5 rounded-full bg-cyan-300" />
                            T.I · REDE 3D
                        </div>
                        <div className="absolute bottom-4 left-4 flex flex-wrap gap-3">
                            {t.ti3d.legend.map((l, i) => (
                                <span
                                    key={l}
                                    className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-slate-400"
                                >
                                    <span
                                        className={`w-1.5 h-1.5 rounded-full ${
                                            i < 3 ? "bg-cyan-300" : "bg-volt"
                                        }`}
                                    />
                                    {l}
                                </span>
                            ))}
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.15} className="flex flex-col gap-4">
                    <div className="rounded-2xl border border-cyan-400/20 bg-surface/70 backdrop-blur-sm p-6 md:p-7">
                        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-4">
                            {t.ti3d.hint}
                        </p>
                        <div className="grid grid-cols-2 gap-2.5 mb-5">
                            {DEST_KEYS.map((key, i) => (
                                <button
                                    key={key}
                                    data-testid={`ti-dest-${key}`}
                                    onClick={() => setDest(key)}
                                    className={`px-4 py-3 rounded-xl text-sm border font-semibold transition-all ${
                                        dest === key
                                            ? "bg-cyan-400 text-obsidian border-cyan-400"
                                            : "border-cyan-400/25 text-slate-300 hover:border-cyan-400/60"
                                    }`}
                                >
                                    {t.ti3d.dests[i]}
                                </button>
                            ))}
                        </div>
                        <button
                            data-testid="ti-send-packet-button"
                            onClick={send}
                            className="group w-full inline-flex items-center justify-center gap-3 bg-volt text-obsidian font-bold px-6 py-4 rounded-full hover:shadow-[0_0_40px_rgba(255,230,0,0.5)] hover:-translate-y-0.5 transition-all"
                        >
                            <Send className="w-4.5 h-4.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            {t.ti3d.send}
                        </button>
                    </div>

                    <div className="rounded-2xl border border-cyan-400/20 bg-surface/70 backdrop-blur-sm p-6 md:p-7 font-mono">
                        <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-5">
                            <Activity className="w-3.5 h-3.5 text-cyan-300" />
                            {t.ti3d.telemetry.title}
                        </p>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between" data-testid="ti-telemetry-packets">
                                <span className="text-slate-500">{t.ti3d.telemetry.packets}</span>
                                <span className="text-cyan-300">{tele.packets}</span>
                            </div>
                            <div className="flex justify-between" data-testid="ti-telemetry-latency">
                                <span className="text-slate-500">{t.ti3d.telemetry.latency}</span>
                                <span className="text-cyan-300">
                                    {tele.latency ? `${tele.latency} ms` : "—"}
                                </span>
                            </div>
                            <div className="flex justify-between" data-testid="ti-telemetry-dest">
                                <span className="text-slate-500">{t.ti3d.telemetry.dest}</span>
                                <span className="text-slate-200">
                                    {tele.dest
                                        ? t.ti3d.dests[DEST_KEYS.indexOf(tele.dest)]
                                        : "—"}
                                </span>
                            </div>
                            <div className="flex justify-between" data-testid="ti-telemetry-nodes">
                                <span className="text-slate-500">{t.ti3d.telemetry.nodes}</span>
                                <span className="text-cyan-300">6 / 6</span>
                            </div>
                        </div>
                        <div className="mt-5 rounded-lg border border-emerald-400/40 bg-emerald-400/10 px-4 py-2.5 text-center text-xs font-semibold tracking-wide text-emerald-400 flex items-center justify-center gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {t.ti3d.telemetry.link}
                        </div>
                        <p className="mt-4 text-[11px] text-slate-500 leading-relaxed flex items-start gap-2 font-sans">
                            <Globe className="w-3.5 h-3.5 text-cyan-300/60 shrink-0 mt-0.5" />
                            {t.ti3d.note}
                        </p>
                    </div>
                </Reveal>
            </div>
        </div>
    );
}
